import { Select, Switch, Space, Spin } from "antd";
import {
  useGetJobQuery,
  useGetSchedulingMachinesQuery,
  useGetMachineGroupsQuery,
  useUpdateJobMutation,
} from "../../../../../redux/api/scheduling";

export type OverrideValue = {
  machine_override: number | null;
  machine_group_override: number | null;
  machine_override_fallback_to_group: boolean;
};

// The fallback toggle is only meaningful when BOTH a machine and a group are set.
export function fallbackEnabled(
  machineId: number | null,
  groupId: number | null,
): boolean {
  return machineId != null && groupId != null;
}

// Build the next override state from a single field change, normalizing the
// fallback flag off whenever the machine+group pair is incomplete.
export function buildOverridePatch(
  prev: OverrideValue,
  change: Partial<OverrideValue>,
): OverrideValue {
  const next: OverrideValue = { ...prev, ...change };
  if (!fallbackEnabled(next.machine_override, next.machine_group_override)) {
    next.machine_override_fallback_to_group = false;
  }
  return next;
}

interface Props {
  jobId: number;
  disabled?: boolean;
}

export default function MachineOverrideSelect({ jobId, disabled }: Props) {
  const { data: job, isLoading } = useGetJobQuery(jobId);
  const { data: machines } = useGetSchedulingMachinesQuery({});
  const { data: groups } = useGetMachineGroupsQuery(undefined);
  const [updateJob, { isLoading: saving }] = useUpdateJobMutation();

  if (isLoading || !job) return <Spin size="small" />;

  const value: OverrideValue = {
    machine_override: job.machine_override ?? null,
    machine_group_override: job.machine_group_override ?? null,
    machine_override_fallback_to_group: !!job.machine_override_fallback_to_group,
  };

  const save = (change: Partial<OverrideValue>) => {
    updateJob({ id: jobId, ...buildOverridePatch(value, change) });
  };

  const machineOptions = (machines ?? []).map((m: any) => ({ label: m.name, value: m.id }));
  const groupOptions = (groups ?? []).map((g: any) => ({ label: g.name, value: g.id }));
  const canFallback = fallbackEnabled(value.machine_override, value.machine_group_override);

  return (
    <Space direction="vertical" size="small">
      <Space wrap>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Auto (scheduler decides)"
          style={{ width: 240 }}
          disabled={disabled || saving}
          value={value.machine_override ?? undefined}
          options={machineOptions}
          onChange={(v) => save({ machine_override: v ?? null })}
          aria-label="Override machine"
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Override machine group"
          style={{ width: 240 }}
          disabled={disabled || saving}
          value={value.machine_group_override ?? undefined}
          options={groupOptions}
          onChange={(v) => save({ machine_group_override: v ?? null })}
          aria-label="Override machine group"
        />
      </Space>
      <Space>
        <Switch
          size="small"
          disabled={disabled || saving || !canFallback}
          checked={value.machine_override_fallback_to_group}
          onChange={(checked) => save({ machine_override_fallback_to_group: checked })}
        />
        <span className="text-slate-500 text-xs">
          Fall back to group if machine is offline (machine takes precedence)
        </span>
      </Space>
    </Space>
  );
}
